import React from 'react';
import {Box, Typography} from '@mui/material';

const red = '#b53d47';
const blue = '#356c92';
const number = value => value == null ? '—' : value.toLocaleString('en-GB', {minimumFractionDigits: 1, maximumFractionDigits: 1});

// Keep labels in evenly spaced slots, ordered by their position along the bar.
export const positionHoursLabels = items => {
  const labels = items.map(item => ({...item})).sort((a, b) => a.target - b.target);
  labels.forEach((label, index) => {
    label.x = (index + 0.5) * 100 / labels.length;
  });
  return labels;
};

export function HoursComparison({totals}) {
  const incomplete = totals.invalidScheduleShifts > 0 || totals.missingProjectionShifts > 0;
  const difference = !incomplete && totals.projectedHours != null && totals.shiftCount
    ? totals.projectedHours - totals.scheduledHours : null;
  const percentage = difference != null && totals.scheduledHours > 0 ? difference / totals.scheduledHours * 100 : null;
  return <Typography component="p" variant="h5" color={difference > 0.05 ? red : difference < -0.05 ? blue : 'text.secondary'}>
    {difference == null ? 'Comparison unavailable'
      : `${difference > 0 ? '+' : ''}${number(difference)} h${percentage == null ? '' : ` (${percentage > 0 ? '+' : ''}${percentage.toFixed(1)}%)`}`}
  </Typography>;
}

export default function HoursColumn({title, totals, period, maximum, hideTitle = false, hideComparison = false}) {
  const past = period === 'past';
  const future = period === 'future';
  const projected = totals.projectedHours;
  const current = totals.missingCurrentShifts > 0 ? null : totals.currentHours;
  const final = past ? projected : current;
  const over = projected != null && projected - totals.scheduledHours > 0.05;
  const incomplete = totals.invalidScheduleShifts > 0 || totals.missingProjectionShifts > 0;
  const scale = Number.isFinite(maximum) && maximum > 0 ? maximum : 1;
  const x = value => Math.max(0, Math.min(1, (value || 0) / scale)) * 100;
  const labels = [
    {name: totals.invalidScheduleShifts > 0 ? 'Scheduled (partial)' : 'Scheduled', value: totals.scheduledHours, colour: '#54616c'},
    ...(!future ? [{name: past ? totals.missingProjectionShifts > 0 ? 'Recorded (partial)' : 'Recorded' : 'Current',
      value: past ? projected : current,
      colour: past && over && !incomplete ? red : blue}] : []),
    ...(!past && !future ? [{name: totals.missingProjectionShifts > 0 ? 'Projected (partial)' : 'Projected', value: projected, colour: over && !incomplete ? red : blue}] : [])
  ];
  const positioned = positionHoursLabels(labels.map(label => ({...label, target: x(label.value)})));
  const description = `${title} hours: ${labels.map(label => `${label.name} ${label.value == null ? 'unavailable' : `${number(label.value)} hours`}`).join('; ')}`;
  const segment = {position: 'absolute', left: 0, top: 0, height: '100%', borderRadius: 'inherit'};
  return <Box sx={{minWidth: 0, width: '100%', boxSizing: 'border-box', border: '1px solid #dce3e8',
    borderRadius: 1, bgcolor: '#f7f9fb', p: 2}}>
    {!hideTitle && <Typography component="h5" variant="subtitle2" sx={{mb: 2, textTransform:'uppercase', fontSize: 12}}>{title}</Typography>}
    <Box role="img" aria-label={description} data-hours-maximum={maximum} sx={{width: '100%'}}>
      <Box sx={{position: 'relative', height: 12, width: '100%', bgcolor: '#edf1f4', borderRadius: 1}}>
        {future && <Box sx={{...segment, width: `${x(totals.scheduledHours)}%`, bgcolor: blue, opacity: 0.2}} />}
        {!past && !future && projected != null && <Box sx={{...segment,
          width: `${x(Math.max(projected, final || 0))}%`, bgcolor: over && !incomplete ? red : blue, opacity: 0.25}} />}
        {!future && final != null && <Box sx={{...segment, width: `${x(final)}%`, bgcolor: past && over && !incomplete ? red : blue}} />}
        {!past && !future && projected != null && <Box sx={{position: 'absolute', left: `${x(projected)}%`, top: 0,
          height: 12, borderLeft: `2px solid ${over && !incomplete ? red : blue}`}} />}
        <Box sx={{position: 'absolute', left: `${x(totals.scheduledHours)}%`, top: -6, height: 24, borderLeft: '2px solid #54616c'}} />
      </Box>
      <Box component="svg" aria-hidden="true" viewBox="0 0 100 36" preserveAspectRatio="none"
        sx={{display: 'block', width: '100%', height: 36, overflow: 'visible'}}>
        {positioned.filter(label => label.value != null).map(label => <path key={label.name}
          d={`M ${label.target} 0 L ${label.target} 14 L ${label.x} 36`} fill="none"
          stroke={label.colour} strokeOpacity="0.5" vectorEffect="non-scaling-stroke" />)}
      </Box>
      <Box sx={{display: 'grid', gridTemplateColumns: `repeat(${positioned.length}, minmax(0, 1fr))`, textAlign: 'center', gap: 1}}>
        {positioned.map(label => <Box key={label.name} sx={{color: label.colour, minWidth: 0}}>
          <Typography component="div" sx={{fontSize: 12, lineHeight: 1.4}}>{label.name}</Typography>
          <Typography component="div" sx={{fontSize: 17, fontWeight: 500, fontVariantNumeric: 'tabular-nums'}}>{number(label.value)} h</Typography>
        </Box>)}
      </Box>
    </Box>
    {!future && !hideComparison && <HoursComparison totals={totals} />}
  </Box>;
}
